import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import moment from "moment";
import { IconButton } from "@mui/material";
import { getCalendarSearchAdvanced } from "../../../../services/Admin/calendar";
import CloseIcon from "@mui/icons-material/Close";

export default function AdvancedSearchButton({ currentView, weekRange }) {
    const [open, setOpen] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);

    const [manualStartTime, setManualStartTime] = useState("");
    const [manualEndTime, setManualEndTime] = useState("");

    const [withTasks, setWithTasks] = useState({});
    const [withoutTasks, setWithoutTasks] = useState({});

    const [selectedWorker, setSelectedWorker] = useState(null); // ← trabajador seleccionado

    const isWeekView = currentView === "timeGridWeek";

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setSelectedDay(null);
        setManualStartTime("");
        setManualEndTime("");
        setSelectedWorker(null);
        setWithTasks({});
        setWithoutTasks({});
        setOpen(false);
    };

    // ------------------------------
    // Formateadores auxiliares
    // ------------------------------

    const formatDate = (d) => {
        if (!d) return "";
        const dateObj = new Date(d);
        const day = dateObj.getDate();
        const monthShort = dateObj.toLocaleString("es-ES", { month: "short" });
        const yearShort = dateObj.getFullYear().toString().slice(-2);
        return `${day}, ${monthShort.charAt(0).toUpperCase() + monthShort.slice(1)} ${yearShort}`;
    };

    const getWeekday = (d) => {
        if (!d) return "";
        return new Date(d).toLocaleDateString("es-ES", { weekday: "short" });
    };

    const getDaysInRange = (start, end) => {
        if (!start || !end) return [];
        const days = [];
        let current = new Date(start);
        const last = new Date(end);
        last.setDate(last.getDate() - 1); // Subtract one day from end
        while (current <= last) {
            days.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }
        return days;
    };

    const handleManualStartTimeChange = (e) => {
        setManualStartTime(e.target.value);
    };

    const handleManualEndTimeChange = (e) => {
        setManualEndTime(e.target.value);
    };

    // ------------------------------
    // Petición a la API con debounce
    // ------------------------------
    useEffect(() => {
        const timeOutId = setTimeout(() => {
            setWithTasks({});
            setWithoutTasks({});

            if (!selectedDay || !manualStartTime || !manualEndTime) return;
            const normalizeTime = (time) => {
                if (!time) return "00:00:00";
                let [h, m] = time.split(":");
                h = h.padStart(2, "0");
                m = m ? m.padStart(2, "0") : "00";
                return `${h}:${m}:00`;
            };

            const formatISODate = (isoString) => {
                const d = new Date(isoString);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
            };

            const from = selectedDay && manualStartTime
                ? `${formatISODate(selectedDay)}T${normalizeTime("00:00")}`
                : null;

            const to = selectedDay && manualEndTime
                ? `${formatISODate(selectedDay)}T${normalizeTime("23:59")}`
                : null;

            if (!from || !to) return;

            getCalendarSearchAdvanced({
                from: moment.utc(from).unix(),
                to: moment.utc(to).unix()
            }).then(res => {
                const data = res?.data || {};

                const withTasks = Object.fromEntries(
                    Object.entries(data).filter(([_, obj]) => Object.keys(obj.tasks).length > 0)
                );

                const withoutTasks = Object.fromEntries(
                    Object.entries(data).filter(([_, obj]) => Object.keys(obj.tasks).length === 0)
                );

                setWithTasks(withTasks);
                setWithoutTasks(withoutTasks);
            });
        }, 800);

        return () => clearTimeout(timeOutId);
    }, [selectedDay, manualStartTime, manualEndTime]);

    return (
        <>
            {/* Botón de abrir modal */}
            <Tooltip
                title={
                    isWeekView
                        ? "Busqueda avanzada"
                        : "Para usar la búsqueda avanzada, selecciona la vista Semana"
                }
                arrow
                placement="top"
            >
                <span>
                    <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        onClick={isWeekView ? handleOpen : undefined}
                        disabled={!isWeekView}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            fontSize: 14,
                            fontWeight: 500,
                            boxShadow: "none",
                            opacity: isWeekView ? 1 : 0.7,
                            pointerEvents: isWeekView ? "auto" : "none",
                            mt: 0.7,
                            minWidth: 90,
                            alignSelf: "flex-start",
                        }}
                    >
                        Avanzada
                    </Button>
                </span>
            </Tooltip>

            {/* Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm" fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                    }
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0,0,0,0.4)",
                            backdropFilter: "blur(5px)",
                        },
                    },
                }}
            >
                <DialogContent style={{ padding: 30, position: "relative", borderRadius: 12 }}>
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ textAlign: "center" }}
                    >
                        Búsqueda avanzada
                    </Typography>

                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: "absolute",
                            top: 25,
                            right: 16,
                            zIndex: 10
                        }}
                        aria-label="Cerrar"
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* ------------------------- */}
                    {/* Selección de día         */}
                    {/* ------------------------- */}
                    <Box sx={{ mt: 2, color: "text.secondary" }}>
                        {weekRange?.start && weekRange?.end ? (
                            <>
                                <div style={{ marginBottom: 8, textAlign: 'left' }}>
                                    <strong>Seleciona un día de la semana</strong>
                                </div>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 1 }}>
                                    {getDaysInRange(weekRange.start, weekRange.end).map((day, idx) => (
                                        <Button
                                            key={day.toISOString()}
                                            variant={selectedDay === day.toISOString() ? "contained" : "outlined"}
                                            color={selectedDay === day.toISOString() ? "primary" : "inherit"}
                                            size="small"
                                            sx={{
                                                width: 173,
                                                height: 36,
                                                fontSize: 13,
                                                borderRadius: 2,
                                                fontWeight: 500,
                                                boxShadow: "none",
                                                opacity: 1,
                                                alignSelf: "flex-start",
                                            }}
                                            onClick={() => setSelectedDay(day.toISOString())}
                                        >
                                            {/* Muestra el día de la semana y la fecha */}
                                            {getWeekday(day)}, {formatDate(day)}
                                        </Button>
                                    ))}
                                </Box>

                                {/* ------------------------- */}
                                {/* Rango horario            */}
                                {/* ------------------------- */}
                                <div style={{ marginTop: 24, marginBottom: 8, textAlign: 'left' }}>
                                    <strong>Selecciona una franja horaria</strong>
                                </div>
                                {/* Dos inputs para hora inicio y fin */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        label="Hora inicio"
                                        variant="outlined"
                                        size="small"
                                        value={manualStartTime}
                                        onChange={handleManualStartTimeChange}
                                        placeholder="Ej: 09:00"
                                        inputProps={{ maxLength: 5 }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: "50%" }}
                                    />
                                    <TextField
                                        label="Hora fin"
                                        variant="outlined"
                                        size="small"
                                        value={manualEndTime}
                                        onChange={handleManualEndTimeChange}
                                        placeholder="Ej: 15:00"
                                        inputProps={{ maxLength: 5 }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: "50%" }}
                                    />
                                </Box>

                                {/* ------------------------- */}
                                {/* Selección trabajador     */}
                                {/* ------------------------- */}
                                <div style={{ marginTop: 24, marginBottom: 8, textAlign: 'left' }}>
                                    <strong>Selecciona un trabajador</strong>
                                </div>

                                <Box
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "#fafafa",
                                        maxHeight: 230,
                                        overflowY: "auto",
                                    }}
                                >
                                    {(Object.entries(withoutTasks).length === 0 && Object.entries(withTasks).length === 0) && (
                                        <Typography sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}>
                                            No se han encontrado trabajadores para los criterios seleccionados.
                                        </Typography>
                                    )}
                                    <List dense>
                                        {/* Trabajadores sin tareas */}
                                        {Object.entries(withoutTasks).map(([workerKey, worker], idx, arr) => (
                                            <React.Fragment key={workerKey}>
                                                <ListItemButton
                                                    selected={selectedWorker === workerKey}
                                                    onClick={() => setSelectedWorker(workerKey)}
                                                    sx={{
                                                        borderRadius: 1,
                                                        mb: 0,
                                                        "&.Mui-selected": {
                                                            backgroundColor: "primary.main",
                                                            color: "#fff",
                                                        },
                                                        "&.Mui-selected:hover": {
                                                            backgroundColor: "primary.light",
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={worker.name || worker.nombre || workerKey}
                                                        primaryTypographyProps={{
                                                            fontWeight: 600,
                                                            fontSize: "0.9rem",
                                                        }}
                                                    />
                                                </ListItemButton>
                                                {/* Línea de separación */}
                                                {idx < arr.length - 1 && (
                                                    <Box sx={{ borderBottom: "1px solid #e0e0e0", mx: 1 }} />
                                                )}
                                            </React.Fragment>
                                        ))}

                                        {/* Trabajadores con tareas */}
                                        {Object.entries(withTasks).map(([workerKey, worker], idx, arr) => (
                                            <React.Fragment key={workerKey}>
                                                <ListItemButton
                                                    selected={selectedWorker === workerKey}
                                                    onClick={() => setSelectedWorker(workerKey)}
                                                    sx={{
                                                        borderRadius: 1,
                                                        mb: 0,
                                                        alignItems: "flex-start",
                                                        "&.Mui-selected": {
                                                            backgroundColor: "primary.light",
                                                            color: "#fff",
                                                        },
                                                        "&.Mui-selected:hover": {
                                                            backgroundColor: "primary.main",
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: "0.9rem",
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {worker.name || worker.nombre || workerKey}
                                                        </Typography>

                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                            {Object.entries(worker.tasks).map(([taskKey, task]) => {
                                                                if (!task.startHour || !task.endHour) return null;

                                                                const start = moment.unix(Number(task.startHour)); 
                                                                const end   = moment.unix(Number(task.endHour));

                                                                let isInRange = false;

                                                                if (manualStartTime && manualEndTime) {
                                                                    const selected = moment(selectedDay);

                                                                    const [h1, m1] = manualStartTime.split(":");
                                                                    const [h2, m2] = manualEndTime.split(":");

                                                                    const manualStart = selected.clone().hour(h1).minute(m1);
                                                                    const manualEnd   = selected.clone().hour(h2).minute(m2);

                                                                    isInRange = start.isBefore(manualEnd) && end.isAfter(manualStart);
                                                                }

                                                                return (
                                                                    <Chip
                                                                        key={taskKey}
                                                                        color={isInRange ? "error" : "default"}
                                                                        size="small"
                                                                        label={`${start.format("HH:mm")} - ${end.format("HH:mm")}`}
                                                                        sx={{
                                                                            fontSize: "0.75rem",
                                                                            height: 24,
                                                                            opacity: isInRange ? 1 : 0.6,
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                </ListItemButton>
                                                {/* Línea de separación */}
                                                {idx < arr.length - 1 && (
                                                    <Box sx={{ borderBottom: "1px solid #e0e0e0", mx: 1 }} />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Box>
                            </>
                        ) : (
                            "Cap setmana seleccionada"
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
